import React, { useState, useEffect, createContext, useContext } from "react";
import API_BASE_URL from "config/baseApi";
const AuthContext = createContext();

const STORAGE_KEY = "hc_auth_user";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw
        ? JSON.parse(raw)
        : { role: "guest", name: null, isAuthenticated: false };
    } catch {
      return { role: "guest", name: null, isAuthenticated: false };
    }
  });

  // Loading/initialization flag to avoid flicker on first load
  const [loading, setLoading] = useState(true);

  // On mount, verify stored token (if any) with the backend and initialize auth state
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
          if (mounted) setLoading(false);
          return;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.token) {
          if (mounted) setLoading(false);
          return;
        }

        // Try to validate token with backend (optional endpoint `/api/auth/me`)
        const resp = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${parsed.token}`,
            Accept: "application/json",
          },
        }).catch(() => null);

        if (resp && resp.ok) {
          const json = await resp.json().catch(() => null);
          const freshUser = json && json.user ? json.user : parsed;
          const u = { ...freshUser, isAuthenticated: true, token: parsed.token };
          if (mounted) {
            setUser(u);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
          }
        } else {
          // Token invalid or endpoint not available: clear stored auth
          if (mounted) {
            const guest = { role: "guest", name: null, isAuthenticated: false };
            setUser(guest);
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (e) {
        // Any error: clear stored auth for safety
        if (mounted) {
          const guest = { role: "guest", name: null, isAuthenticated: false };
          setUser(guest);
          localStorage.removeItem(STORAGE_KEY);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => {
      mounted = false;
    };
  }, []);

  // Accepts an API response: { token, user }
  const login = ({ token, user: userObj, name, role } = {}) => {
    // Backwards-compatible fallback for mock login calls
    if (!userObj) {
      const u = { name: name || "Demo User", role: role || "staff", isAuthenticated: true };
      setUser(u);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
      return;
    }

    const u = {
      ...userObj,
      isAuthenticated: true,
      token,
    };
    setUser(u);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
  };

  const logout = () => {
    const guest = { role: "guest", name: null, isAuthenticated: false };
    setUser(guest);
    localStorage.removeItem(STORAGE_KEY);
  };

  const hasRole = (roles = []) => {
    if (!roles || roles.length === 0) return true;
    return roles.includes(user.role);
  };

  const hasPermission = (requiredPermissions = []) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    if (!user || !user.permissions) return false;
    const keys = user.permissions.map((p) => p.key_name);
    return requiredPermissions.some((rp) => keys.includes(rp));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, hasRole, hasPermission, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;